// Types for Store Hours Management

export type DayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

export type OccasionType = 'DELIVERY' | 'CARRYOUT' | 'DINE_IN' | 'DRIVE_THRU' | 'CATERING_CARRYOUT';

export interface TimeSlot {
  open: string;
  close: string;
}

export interface SplitShift extends TimeSlot {
  open2?: string;
  close2?: string;
}

export interface DaySchedule {
  enabled: boolean;
  timeSlot: SplitShift;
}

export interface TimeFrame {
  startDate?: string;
  endDate?: string;
  intervalStartTime: string;
  duration: number;
  days: DayOfWeek[];
  blackout: boolean;
  weight: number;
  offsetOpen: number;
  offsetClose: number;
}

export interface Occasion {
  occasion: OccasionType;
  isEnabled: boolean;
  hours: TimeFrame[];
  serviceTime?: number;
}

export interface StoreHours {
  storeNumber: string;
  storeName: string;
  timezone: string;
  occasions: Occasion[];
  lastModified?: string;
  pendingChanges?: boolean;
  isSelected?: boolean;
}

export interface DayGroup {
  days: DayOfWeek[];
  schedule: DaySchedule;
  label: string; // e.g., "Mon-Fri", "Weekends", "Daily"
}

export interface BulkEditData {
  selectedStores: string[];
  selectedOccasion: OccasionType;
  dayGroups: DayGroup[];
  applyToAll: boolean;
  template?: string;
}

export interface HoursTemplate {
  id: string;
  name: string;
  description?: string;
  dayGroups: DayGroup[];
  occasions: OccasionType[];
}

// Unified Hours Management - Option C: Primary with Inheritance
export type InheritanceMode = 'inherit' | 'override';

export interface OccasionInheritance {
  occasion: OccasionType;
  mode: InheritanceMode;
  // Only populated when mode is 'override'
  customSchedule?: Record<DayOfWeek, DayScheduleV2>;
}

export interface DayScheduleV2 {
  enabled: boolean;
  timeSlots: TimeSlot[];
}

// Special hours entry for holidays, closures, etc.
export interface SpecialHoursEntryV2 {
  id?: string;
  startDate: string; // YYYY-MM-DD format
  endDate: string; // YYYY-MM-DD format
  isClosed: boolean;
  openTime?: string; // HH:MM format
  closeTime?: string; // HH:MM format
}

export interface UnifiedHoursState {
  primaryOccasion: OccasionType;
  primarySchedule: Record<DayOfWeek, DayScheduleV2>;
  occasionSettings: Record<OccasionType, OccasionInheritance>;
  // Special hours for each occasion (key = occasion, value = array of special hours)
  specialHours?: Record<OccasionType, SpecialHoursEntryV2[]>;
}

// Helper constants
export const DAYS_ORDER: DayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

// Full order of all occasions (used for sorting)
export const OCCASION_DISPLAY_ORDER: OccasionType[] = ['CARRYOUT', 'DRIVE_THRU', 'DINE_IN', 'DELIVERY', 'CATERING_CARRYOUT'];

// Order to display occasions in the UI - filtered by available occasions from env var
// Uses a getter function to avoid circular dependency issues at module load time
export function getAvailableOccasionOrder(): OccasionType[] {
  // Dynamic import to avoid circular dependency
  const { AVAILABLE_OCCASIONS } = require('../../constants');
  return OCCASION_DISPLAY_ORDER.filter(o => AVAILABLE_OCCASIONS.includes(o));
}

// Static OCCASION_ORDER for backwards compatibility - computed once at module load
// Note: This works because constants.ts no longer imports from this file
import { AVAILABLE_OCCASIONS } from '../../constants';
export const OCCASION_ORDER: OccasionType[] = OCCASION_DISPLAY_ORDER.filter(o => AVAILABLE_OCCASIONS.includes(o));

export const DAY_LABELS: Record<DayOfWeek, string> = {
  'MONDAY': 'Monday',
  'TUESDAY': 'Tuesday',
  'WEDNESDAY': 'Wednesday',
  'THURSDAY': 'Thursday',
  'FRIDAY': 'Friday',
  'SATURDAY': 'Saturday',
  'SUNDAY': 'Sunday'
};

export const DAY_LABELS_SHORT: Record<DayOfWeek, string> = {
  'MONDAY': 'Mon',
  'TUESDAY': 'Tue',
  'WEDNESDAY': 'Wed',
  'THURSDAY': 'Thu',
  'FRIDAY': 'Fri',
  'SATURDAY': 'Sat',
  'SUNDAY': 'Sun'
};

export const OCCASION_LABELS: Record<OccasionType, string> = {
  'DELIVERY': 'Delivery',
  'CARRYOUT': 'Carry Out',
  'DINE_IN': 'Dine In',
  'DRIVE_THRU': 'Drive-Thru',
  'CATERING_CARRYOUT': 'Catering'
};

export const OCCASION_ICONS: Record<OccasionType, string> = {
  'DELIVERY': '🚚',
  'CARRYOUT': '🛍️',
  'DINE_IN': '🍽️',
  'DRIVE_THRU': '🚗',
  'CATERING_CARRYOUT': '🎉'
};

/**
 * Formats an occasion code into a human-readable display name.
 * Handles various backend formats and normalizes them consistently.
 *
 * @param occasion - The occasion code from backend (e.g., 'DRIVE_THRU', 'CARRYOUT', 'DINE_IN')
 * @returns Human-readable name (e.g., 'Drive-Thru', 'Carry Out', 'Dine In')
 */
export function formatOccasionName(occasion: string): string {
  // Normalize to uppercase for comparison
  const normalized = occasion.toUpperCase();

  // Check against known occasion types first
  if (normalized in OCCASION_LABELS) {
    return OCCASION_LABELS[normalized as OccasionType];
  }

  // Handle legacy/alternate formats
  switch (normalized) {
    case 'DINEIN':
      return 'Dine In';
    case 'CARRY_OUT':
      return 'Carry Out';
    case 'DRIVETHRU':
      return 'Drive-Thru';
    case 'CURBSIDE':
      return 'Curbside';
    case 'CATERING':
      return 'Catering';
    default:
      // Fallback: convert SNAKE_CASE to Title Case with hyphens for multi-word
      return occasion
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
  }
}